import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

/**
 * Single sort item with explicit order
 */
export type SortItem = {
  id: string;
  order: "asc" | "desc";
};

/**
 * Sorting state as an array of sort items
 */
export type SortingState = Array<SortItem>;

type SortingStore = Map<string, SortingState>;

interface AtomTableSortingContextValue {
  getSorting: (key: string) => SortingState | undefined;
  setSorting: (key: string, sorting: SortingState) => void;
}

const AtomTableSortingContext =
  createContext<AtomTableSortingContextValue | null>(null);

/**
 * Provider for AtomTable sorting state persistence across navigation.
 * Place this at the root of your app to enable session-level sorting persistence.
 */
export function AtomTableSortingProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Use ref to avoid re-renders when sorting changes
  const storeRef = useRef<SortingStore>(new Map());

  const getSorting = useCallback((key: string): SortingState | undefined => {
    return storeRef.current.get(key);
  }, []);

  const setSorting = useCallback((key: string, sorting: SortingState): void => {
    storeRef.current.set(key, sorting);
  }, []);

  const value = useMemo(
    () => ({ getSorting, setSorting }),
    [getSorting, setSorting],
  );

  return (
    <AtomTableSortingContext.Provider value={value}>
      {children}
    </AtomTableSortingContext.Provider>
  );
}

/**
 * Hook to access the AtomTable sorting context.
 * Returns null if used outside of AtomTableSortingProvider.
 */
export function useAtomTableSortingContext(): AtomTableSortingContextValue | null {
  return useContext(AtomTableSortingContext);
}
